import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {fundraisersApi} from '../api';
import {
  FundraiserDetailedModel,
  FundraiserFinanceModel,
  UpdateFundraiserDTO,
  UpdateFundraiserFinanceDTO,
  BenefactorTransferDTO,
  ProjectTransferDTO,
} from '../model';
import {useNotifications} from '../../../shared';

type FundraiserByIdState = {
  getFundraiserById: UseQueryResult<FundraiserDetailedModel, Error>;
  getFundraiserFinance: UseQueryResult<FundraiserFinanceModel, Error>;
  makeNonUrgentMutation: UseMutationResult<void, Error, void>;
  makeUrgentMutation: UseMutationResult<void, Error, void>;
  deleteFundraiserMutation: UseMutationResult<void, Error, void>;
  updateFundraiserMutation: UseMutationResult<any, Error, UpdateFundraiserDTO>;
  updateFundraiserFinanceMutation: UseMutationResult<FundraiserFinanceModel, Error, UpdateFundraiserFinanceDTO>;
  publishFundraiserMutation: UseMutationResult<void, Error, void>;
  unpublishFundraiserMutation: UseMutationResult<void, Error, void>;
  markAmountCollectedMutation: UseMutationResult<void, Error, void>;
  completeFundraiserMutation: UseMutationResult<void, Error, void>;
  transferFromBenefactorMutation: UseMutationResult<void, Error, BenefactorTransferDTO>;
  transferFromProjectMutation: UseMutationResult<void, Error, ProjectTransferDTO>;
  refetchFundraiser: () => void;
  refetchFinance: () => void;
};

interface UseFundraiserProps {
  id: string;
}

/**
 * Используется для получения данных о сборе по id
 */
export const useFundraiser = ({id}: UseFundraiserProps): FundraiserByIdState => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const getFundraiserById = useQuery<FundraiserDetailedModel, Error>({
    queryKey: ['fundraiser', id],
    queryFn: () => fundraisersApi.getFundraiserDetailedById(id),
    enabled: !!id,
  });

  const getFundraiserFinance = useQuery<FundraiserFinanceModel, Error>({
    queryKey: ['fundraiser-finance', id],
    queryFn: () => fundraisersApi.getFundraiserFinance(id),
    enabled: !!id,
  });

  const refetchFundraiser = () => {
    queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
  };

  const refetchFinance = () => {
    queryClient.invalidateQueries({queryKey: ['fundraiser-finance', id]});
  };

  const makeNonUrgentMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.makeNonUrgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
    },
  });

  const makeUrgentMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.makeUrgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
    },
  });

  const deleteFundraiserMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.deleteFundraiser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
      createNotification({
        title: 'Сбор успешно удален',
        name: 'deleteFundraiser',
        theme: 'success',
      });
    },
  });

  const updateFundraiserMutation = useMutation<any, Error, UpdateFundraiserDTO>({
    mutationFn: (data: UpdateFundraiserDTO) => fundraisersApi.updateFundraiser({fundraiserId: id, data}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
    },
  });

  const updateFundraiserFinanceMutation = useMutation<FundraiserFinanceModel, Error, UpdateFundraiserFinanceDTO>({
    mutationFn: (data: UpdateFundraiserFinanceDTO) => fundraisersApi.updateFundraiserFinance(id, data),
    onSuccess: () => {
      refetchFinance();
    },
  });

  const publishFundraiserMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.publishFundraiser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
    },
  });

  const unpublishFundraiserMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.unpublishFundraiser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
    },
  });

  const transferFromBenefactorMutation = useMutation<void, Error, BenefactorTransferDTO>({
    mutationFn: (transferData: BenefactorTransferDTO) => fundraisersApi.addBenefactorTransfer(id, transferData),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser-finance', id]});
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      createNotification({
        title: 'Средства успешно внесены',
        name: 'transferFromBenefactor',
        theme: 'success',
      });
    },
  });

  const transferFromProjectMutation = useMutation<void, Error, ProjectTransferDTO>({
    mutationFn: (transferData: ProjectTransferDTO) => fundraisersApi.addProjectTransfer(id, transferData),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser-finance', id]});
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      createNotification({
        title: 'Средства проекта успешно внесены',
        name: 'transferFromProject',
        theme: 'success',
      });
    },
  });

  const markAmountCollectedMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.markAmountCollected(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
      queryClient.invalidateQueries({queryKey: ['fundraiser-finance', id]});
      createNotification({
        title: 'Средства отмечены как собранные',
        name: 'markAmountCollected',
        theme: 'success',
      });
    },
  });

  const completeFundraiserMutation = useMutation<void, Error, void>({
    mutationFn: () => fundraisersApi.completeFundraiser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['fundraiser', id]});
      queryClient.invalidateQueries({queryKey: ['fundraisers']});
      queryClient.invalidateQueries({queryKey: ['fundraiser-finance', id]});
      createNotification({
        title: 'Сбор завершен',
        name: 'completeFundraiser',
        theme: 'success',
      });
    },
  });

  return {
    getFundraiserById,
    getFundraiserFinance,
    makeNonUrgentMutation,
    makeUrgentMutation,
    deleteFundraiserMutation,
    updateFundraiserMutation,
    updateFundraiserFinanceMutation,
    publishFundraiserMutation,
    unpublishFundraiserMutation,
    markAmountCollectedMutation,
    completeFundraiserMutation,
    transferFromBenefactorMutation,
    transferFromProjectMutation,
    refetchFundraiser,
    refetchFinance,
  };
};
